import {
  createEffect,
  createMemo,
  createResource,
  createSignal,
  For,
  on,
  Show,
} from "solid-js";
import type { MeasurementPoint, SeriesPoint } from "@energyplatform/client";
import { api } from "../services.ts";
import { locale, t, tOr } from "../i18n.ts";
import { formatDateTime, formatNumber, formatShare } from "../format.ts";
import { range } from "../context.ts";
import { Chart, type ChartSeries } from "./Chart.tsx";
import { ErrorBox } from "./ErrorBox.tsx";
import { Skeleton } from "./ui.tsx";
import { AddToReport } from "./AddToReport.tsx";
import { Icon } from "../icons.tsx";

/** Nominal voltage and the EN 50160 band of ±10% around it. */
export const NOMINAL_V = 230;
export const LOW_V = 207;
export const HIGH_V = 253;
const QUARTER = 900_000;

/** Order of the parameters as an engineer reads a meter. */
const ORDER = [
  "voltage",
  "current",
  "power.active",
  "power.reactive",
  "power.apparent",
  "power.factor",
  "frequency",
  "energy.import",
  "energy.export",
  "energy.reactive.import",
  "energy.reactive.export",
];
const CHANNEL_ORDER = ["main", "phase1", "phase2", "phase3"];

/** A counter (energy register) is charted as its increments per quarter hour; the rest as values with min and max. */
export const isCounter = (attribute: string) => attribute.startsWith("energy.");

export interface ParamStats {
  min: number | null;
  avg: number | null;
  max: number | null;
  /** Voltage only: share of quarter hours whose mean was outside 207–253 V. */
  outside: number | null;
  /** Voltage only: quarter hours with a minimum below 207 V (dips) and a maximum above 253 V (swells). */
  dips: number;
  swells: number;
  /** Counters: the increase over the period. */
  sum: number | null;
}

/** Statistics of one parameter's quarter hours; voltage gets the EN 50160 figures. */
export function paramStats(
  attribute: string,
  points: SeriesPoint[],
): ParamStats {
  if (isCounter(attribute)) {
    const inc = increments(points)
      .map((p) => p.v)
      .filter((v): v is number => v !== null);
    return {
      min: null,
      avg: null,
      max: null,
      outside: null,
      dips: 0,
      swells: 0,
      sum: inc.length === 0 ? null : inc.reduce((a, b) => a + b, 0),
    };
  }
  const values = points
    .map((p) => p.value)
    .filter((v): v is number => v !== null);
  const mins = points
    .map((p) => p.min ?? p.value)
    .filter((v): v is number => v !== null);
  const maxs = points
    .map((p) => p.max ?? p.value)
    .filter((v): v is number => v !== null);
  const voltage = attribute === "voltage";
  return {
    min: mins.length ? Math.min(...mins) : null,
    avg: values.length
      ? values.reduce((a, b) => a + b, 0) / values.length
      : null,
    max: maxs.length ? Math.max(...maxs) : null,
    outside:
      voltage && values.length
        ? values.filter((v) => v < LOW_V || v > HIGH_V).length / values.length
        : null,
    dips: voltage
      ? points.filter((p) => (p.min ?? p.value ?? NOMINAL_V) < LOW_V).length
      : 0,
    swells: voltage
      ? points.filter((p) => (p.max ?? p.value ?? NOMINAL_V) > HIGH_V).length
      : 0,
    sum: null,
  };
}

/** Increase of a counter per bucket (the bucket's highest reading minus the previous one's); a reset or a gap is a gap. */
export function increments(
  points: SeriesPoint[],
): { t: number; v: number | null }[] {
  const out: { t: number; v: number | null }[] = [];
  let previous: number | null = null;
  for (const p of points) {
    const now = p.max ?? p.value;
    out.push({
      t: new Date(p.at).getTime(),
      v:
        now !== null && previous !== null && now >= previous
          ? now - previous
          : null,
    });
    if (now !== null) {
      previous = now;
    }
  }
  return out;
}

interface Pick {
  channel: string;
  attribute: string;
  unit: string;
}

const key = (p: { channel: string; attribute: string }) =>
  `${p.channel}/${p.attribute}`;
/** Each channel keeps its colour in every panel: L1, L2, L3 as on the switchboard, the total apart. */
const CHANNEL_COLOR: Record<string, string> = {
  phase1: "var(--s1)",
  phase2: "var(--s2)",
  phase3: "var(--s3)",
  main: "var(--s4)",
};
const color = (channel: string) => CHANNEL_COLOR[channel] ?? "var(--s5)";
const byOrder = (order: string[]) => (a: string, b: string) =>
  (order.indexOf(a) + 1 || 99) - (order.indexOf(b) + 1 || 99);

/** One meter per device: its points (the total and each phase) are channels of the same meter. */
function metersOf(points: MeasurementPoint[]) {
  const byDevice = new Map<string, MeasurementPoint[]>();
  for (const p of points) {
    if (
      p.binding &&
      (p.medium === "el" || p.medium === "pv" || p.medium === "bess")
    ) {
      byDevice.set(p.binding.deviceId, [
        ...(byDevice.get(p.binding.deviceId) ?? []),
        p,
      ]);
    }
  }
  return [...byDevice.entries()]
    .map(([deviceId, list]) => {
      const main =
        list.find((p) => p.binding!.channel === "main") ??
        list.find((p) => p.role === "main") ??
        list[0]!;
      return {
        deviceId,
        name: list.length > 1 ? main.name.split(" · ")[0]!.trim() : main.name,
        pointIds: list.map((p) => p.id),
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * "Parametry licznika": every quantity a meter reports — voltage, current, active, reactive and apparent power, power
 * factor, frequency, active and reactive energy. The quantities are tiles with their value now (click to chart them),
 * the channels (total, L1, L2, L3) a switch shared by all of them; each quantity gets its own chart of quarter-hour
 * means with their minimum and maximum. Voltage is drawn against the 207–253 V band of EN 50160 with the time outside
 * it, dips and swells.
 */
export function MeterParameters(props: {
  points: MeasurementPoint[];
  preferred?: string;
}) {
  const meters = createMemo(() => metersOf(props.points));
  const [picked, setDeviceId] = createSignal<string>();
  // The meter chosen, else the one of the preferred point, else the first; the points may arrive after mounting.
  const deviceId = () =>
    meters().find((m) => m.deviceId === picked())?.deviceId ??
    meters().find((m) => m.pointIds.includes(props.preferred ?? ""))
      ?.deviceId ??
    meters()[0]?.deviceId ??
    "";
  const [latest] = createResource(
    () => deviceId() || undefined,
    (id) => api.devices.latest(id),
  );
  const available = createMemo(() => {
    const list = (latest() ?? []).filter(
      (v) => v.number !== null || v.text === null,
    );
    const attrs = [...new Set(list.map((v) => v.attribute))].sort(
      byOrder(ORDER),
    );
    const channels = [...new Set(list.map((v) => v.channel))].sort(
      byOrder(CHANNEL_ORDER),
    );
    return { list, attrs, channels };
  });
  const [attrs, setAttrs] = createSignal<string[]>([]);
  const [adding, setAdding] = createSignal(false);
  const [channels, setChannels] = createSignal<string[]>([]);
  // A new meter starts with its voltages per phase (else its first quantity).
  createEffect(
    on(available, (a) => {
      setAttrs(
        a.attrs.includes("voltage")
          ? ["voltage"]
          : a.attrs.length > 0
            ? [a.attrs[0]!]
            : [],
      );
      const phases = a.channels.filter((c) => c.startsWith("phase"));
      setChannels(phases.length > 0 ? phases : a.channels);
    }),
  );
  const flip = (list: string[], x: string) =>
    list.includes(x) ? list.filter((y) => y !== x) : [...list, x];
  const channelsOf = (attribute: string) =>
    available()
      .list.filter((v) => v.attribute === attribute)
      .map((v) => v.channel)
      .sort(byOrder(CHANNEL_ORDER));
  // The chosen channels a quantity has; one only reported in total (energy registers) is shown in total.
  const chosen = createMemo<Pick[]>(() =>
    attrs()
      .slice()
      .sort(byOrder(ORDER))
      .flatMap((a) => {
        const own = channelsOf(a);
        const shown = own.filter((c) => channels().includes(c));
        return (shown.length > 0 ? shown : own).map((c) => ({
          channel: c,
          attribute: a,
          unit: available().list.find(
            (v) => v.attribute === a && v.channel === c,
          )!.unit,
        }));
      }),
  );
  const nowText = (attribute: string) => {
    const own = channelsOf(attribute);
    const phases = own.filter((c) => c.startsWith("phase"));
    const shown =
      phases.length > 0 && !isCounter(attribute) ? phases : own.slice(0, 1);
    const values = shown
      .map((c) =>
        available().list.find(
          (v) => v.attribute === attribute && v.channel === c,
        ),
      )
      .filter((v) => v && v.number !== null);
    if (values.length === 0) {
      return "—";
    }
    const unit = values[0]!.unit === "1" ? "" : values[0]!.unit;
    const digits =
      isCounter(attribute) || attribute === "voltage"
        ? 0
        : attribute === "power.factor"
          ? 2
          : 1;
    return `${values.map((v) => formatNumber(v!.number, locale(), digits)).join(" / ")} ${unit}`.trim();
  };

  const [series] = createResource(
    () =>
      deviceId() && chosen().length > 0
        ? { device: deviceId(), picks: chosen(), ...range() }
        : undefined,
    async (q) =>
      Promise.all(
        q.picks.map(async (pick) => ({
          pick,
          points: await api.devices.series(
            q.device,
            pick.channel,
            pick.attribute,
            q.from,
            q.to,
            "15m",
          ),
        })),
      ),
  );
  // One panel per quantity, in the order of the tiles.
  const panels = createMemo(() => {
    const groups = new Map<string, { pick: Pick; points: SeriesPoint[] }[]>();
    for (const s of series() ?? []) {
      groups.set(s.pick.attribute, [
        ...(groups.get(s.pick.attribute) ?? []),
        s,
      ]);
    }
    return [...groups.entries()];
  });
  const label = (p: Pick) =>
    `${tOr(`attribute.${p.attribute}`, p.attribute)} · ${tOr(`channel.${p.channel}`, p.channel)}`;
  const num = (v: number | null, d = 1) =>
    v === null ? "—" : formatNumber(v, locale(), d);

  return (
    <div class="card-b" style={{ display: "grid", gap: "14px" }}>
      <div class="par-head">
        <div class="field" style={{ "min-width": "220px", flex: "1 1 260px" }}>
          <label for="mpPoint">{t("analysis.par.meter")}</label>
          <select
            id="mpPoint"
            value={deviceId()}
            onChange={(e) => setDeviceId(e.currentTarget.value)}
          >
            <For each={meters()}>
              {(m) => <option value={m.deviceId}>{m.name}</option>}
            </For>
          </select>
        </div>
        <Show when={available().channels.length > 1}>
          <div class="field">
            <span class="eyebrow">{t("analysis.par.channels")}</span>
            <div
              class="seg"
              role="group"
              aria-label={t("analysis.par.channels")}
            >
              <For each={available().channels}>
                {(c) => (
                  <button
                    type="button"
                    aria-pressed={channels().includes(c)}
                    onClick={() => setChannels(flip(channels(), c))}
                  >
                    <span class="par-dot" style={{ background: color(c) }} />
                    {tOr(`channel.${c}`, c)}
                  </button>
                )}
              </For>
            </div>
          </div>
        </Show>
        <button
          type="button"
          class="btn"
          style={{ "margin-left": "auto" }}
          disabled={chosen().length === 0}
          onClick={() => setAdding(true)}
        >
          <Icon name="file" />
          {t("analysis.toReport")}
        </button>
      </div>
      <Show when={adding()}>
        <AddToReport
          series={chosen()
            .slice(0, 8)
            .map((p) => ({
              source: "point" as const,
              pointId:
                meters().find((m) => m.deviceId === deviceId())?.pointIds[0] ??
                null,
              attribute: p.attribute,
              channel: p.channel,
            }))}
          title={`${meters().find((m) => m.deviceId === deviceId())?.name ?? ""} · ${attrs()
            .map((a) => tOr(`attribute.${a}`, a))
            .join(", ")}`.slice(0, 120)}
          onClose={() => setAdding(false)}
        />
      </Show>

      <Show when={!latest.error} fallback={<ErrorBox error={latest.error} />}>
        <Show when={latest()} fallback={<Skeleton lines={3} />}>
          <Show
            when={available().attrs.length > 0}
            fallback={<p class="hint">{t("analysis.par.none")}</p>}
          >
            <div
              class="par-tiles"
              role="group"
              aria-label={t("analysis.par.quantities")}
            >
              <For each={available().attrs}>
                {(a) => (
                  <button
                    type="button"
                    class="par-tile"
                    aria-pressed={attrs().includes(a)}
                    onClick={() => setAttrs(flip(attrs(), a))}
                  >
                    <span class="nm">{tOr(`attribute.${a}`, a)}</span>
                    <span class="v">{nowText(a)}</span>
                  </button>
                )}
              </For>
            </div>
          </Show>
        </Show>
      </Show>

      <Show
        when={chosen().length > 0}
        fallback={
          <p class="hint" style={{ margin: 0 }}>
            {t("analysis.par.pick")}
          </p>
        }
      >
        <Show when={!series.error} fallback={<ErrorBox error={series.error} />}>
          <Show when={series()} fallback={<Skeleton lines={6} />}>
            <For each={panels()}>
              {([attribute, items]) => {
                const voltage = attribute === "voltage";
                const counter = isCounter(attribute);
                const unit = counter
                  ? `${items[0]!.pick.unit}/15 min`
                  : items[0]!.pick.unit;
                const at = (p: SeriesPoint) =>
                  new Date(p.at).getTime() + QUARTER / 2;
                const chart = (): ChartSeries[] =>
                  items.flatMap((i): ChartSeries[] => {
                    const c = color(i.pick.channel);
                    const name = tOr(
                      `channel.${i.pick.channel}`,
                      i.pick.channel,
                    );
                    if (counter) {
                      return [
                        {
                          key: key(i.pick),
                          label: name,
                          color: c,
                          kind: "bar" as const,
                          points: increments(i.points),
                        },
                      ];
                    }
                    const main: ChartSeries = {
                      key: key(i.pick),
                      label: name,
                      color: c,
                      kind: "line",
                      points: i.points.map((p) => ({ t: at(p), v: p.value })),
                    };
                    // With one channel the quarter's minimum and maximum show short dips the mean hides.
                    return items.length === 1
                      ? [
                          main,
                          {
                            key: `${key(i.pick)}/min`,
                            label: `${name} · min`,
                            color: c,
                            kind: "line" as const,
                            dotted: true,
                            points: i.points.map((p) => ({
                              t: at(p),
                              v: p.min,
                            })),
                          },
                          {
                            key: `${key(i.pick)}/max`,
                            label: `${name} · max`,
                            color: c,
                            kind: "line" as const,
                            dotted: true,
                            points: i.points.map((p) => ({
                              t: at(p),
                              v: p.max,
                            })),
                          },
                        ]
                      : [main];
                  });
                return (
                  <section class="par-panel">
                    <h3>
                      {tOr(`attribute.${attribute}`, attribute)}
                      <span class="hint">
                        {" "}
                        · {unit === "1" ? "cos φ" : unit}
                      </span>
                    </h3>
                    <Chart
                      series={chart()}
                      from={range().from}
                      to={range().to}
                      unit={unit === "1" ? "" : unit}
                      locale={locale()}
                      bucketMs={QUARTER}
                      tight={["voltage", "frequency", "power.factor"].includes(
                        attribute,
                      )}
                      height={220}
                      emptyText={t("analysis.par.noData")}
                      thresholds={
                        voltage
                          ? [
                              {
                                value: LOW_V,
                                label: t("analysis.par.low", { v: LOW_V }),
                              },
                              {
                                value: HIGH_V,
                                label: t("analysis.par.high", { v: HIGH_V }),
                              },
                            ]
                          : undefined
                      }
                    />
                  </section>
                );
              }}
            </For>
            <div class="scrollx">
              <table class="tbl">
                <thead>
                  <tr>
                    <th>{t("analysis.par.parameter")}</th>
                    <th class="r">min</th>
                    <th class="r">{t("analysis.par.avg")}</th>
                    <th class="r">max</th>
                    <th class="r">{t("analysis.par.sum")}</th>
                    <th class="r">{t("analysis.par.outside")}</th>
                    <th class="r">{t("analysis.par.dips")}</th>
                    <th class="r">{t("analysis.par.swells")}</th>
                  </tr>
                </thead>
                <tbody>
                  <For each={series()}>
                    {(s) => {
                      const st = paramStats(s.pick.attribute, s.points);
                      const worst = () =>
                        s.points.reduce<SeriesPoint | null>(
                          (w, p) =>
                            (p.min ?? Infinity) < (w?.min ?? Infinity) ? p : w,
                          null,
                        );
                      return (
                        <tr>
                          <td>
                            {label(s.pick)}
                            <Show
                              when={
                                s.pick.attribute === "voltage" &&
                                st.dips > 0 &&
                                worst()
                              }
                            >
                              {(w) => (
                                <div class="hint">
                                  {t("analysis.par.lowest", {
                                    v: num(w().min, 1),
                                    at: formatDateTime(w().at, locale()),
                                  })}
                                </div>
                              )}
                            </Show>
                          </td>
                          <td class="r num">{num(st.min, 2)}</td>
                          <td class="r num">{num(st.avg, 2)}</td>
                          <td class="r num">{num(st.max, 2)}</td>
                          <td class="r num">
                            {st.sum === null
                              ? "—"
                              : `${num(st.sum, 2)} ${s.pick.unit}`}
                          </td>
                          <td class="r num">
                            {st.outside === null
                              ? "—"
                              : formatShare(st.outside, locale())}
                          </td>
                          <td class="r num">
                            {s.pick.attribute === "voltage" ? st.dips : "—"}
                          </td>
                          <td class="r num">
                            {s.pick.attribute === "voltage" ? st.swells : "—"}
                          </td>
                        </tr>
                      );
                    }}
                  </For>
                </tbody>
              </table>
            </div>
            <p class="hint" style={{ margin: 0 }}>
              {t("analysis.par.hint")}
            </p>
          </Show>
        </Show>
      </Show>
    </div>
  );
}
